import {
  personBelowActiveFallback,
  personBelowActiveSentence,
  personBelowActiveTitle,
  personCapacitySentence,
  personCapacityTitle,
  personKindOfBelowActiveCode,
} from "@/shared/utils/person-quantity";
import { staffAbovePeopleSentence } from "@/shared/utils/staff-quantity";

/** Error-code catalogue mirrored from @nacs/shared `api/errors.ts` (clients map codes, never message strings). */
export const ERROR_CODES = [
  "BAD_REQUEST",
  "UNAUTHORIZED",
  // ── ADR-0011 addendum 2026-10-04 (plans/v2/stay-signed-in.md): why a session ended, and the organisation pin ──
  "MEMBERSHIP_INACTIVE",
  "TENANT_DELETED",
  "ORG_MISMATCH",
  "RELOAD_REQUIRED",
  "FORBIDDEN",
  "NOT_FOUND",
  "CONFLICT",
  "VALIDATION_ERROR",
  "RATE_LIMITED",
  "PLAN_LIMIT_REACHED",
  "DEVICE_OFFLINE",
  "DEVICE_DISABLED",
  "DEVICE_ALREADY_PAIRED",
  "DEVICE_REJECTED",
  "GATEWAY_UNAVAILABLE",
  "PAYMENT_GATEWAY_UNAVAILABLE",
  "RANGE_TOO_WIDE",
  "OTP_INVALID",
  "OTP_EXPIRED",
  "OTP_LOCKED",
  "OTP_RATE_LIMITED",
  "SMS_UNAVAILABLE",
  "INVITE_NOT_FOUND",
  "INVITE_NOT_OPEN",
  "INVITE_EXPIRED",
  "MEMBER_GUARD",
  "ACCOUNT_PROTECTED",
  "ACCOUNT_LAST_MANAGER",
  "ROLE_IN_USE",
  "CARD_IN_USE",
  "PHOTO_INVALID",
  // Kumar 2026-10-08: the upload-time face check refused the photo (every organisation).
  "FACE_PHOTO_UNUSABLE",
  "IMPORT_INVALID",
  "API_KEY_INVALID",
  "TRIAL_EXPIRED",
  "TENANT_SUSPENDED",
  "TENANT_DELETING",
  "IDEMPOTENCY_MISMATCH",
  "IDEMPOTENCY_IN_PROGRESS",
  "OPERATOR_REQUIRED",
  // ── billing: the profile and the guard that survived M7 (ADR-0012) ──
  "BILLING_PROFILE_REQUIRED",
  "PLAN_DOWNGRADE_BLOCKED",
  // ── M11 catalogue, prepaid quantities and orders (ADR-0015) ──
  "ADDON_REQUIRED",
  "CATALOG_ITEM_UNKNOWN",
  "CATALOG_ITEM_INACTIVE",
  "CATALOG_PRICE_MISSING",
  "CATALOG_TAX_MISSING",
  "QUANTITY_OUT_OF_RANGE",
  "ORDER_NOT_FOUND",
  "ORDER_NOT_PAYABLE",
  "PERIOD_CHANGE_PENDING",
  // ── M11 §14: timing choices and cancelling the subscription (ADR-0015 addendum) ──
  "PERIOD_CHANGE_AT_RENEWAL_ONLY",
  "SUBSCRIPTION_IN_TRIAL",
  "SUBSCRIPTION_NOT_ACTIVE",
  // ── M11 follow-up: renewing early (plans/v1/M11-subscription-redesign.md §4) ──
  "SUBSCRIPTION_CANCELLING",
  "ORDER_OPEN",
  "ITEM_NOT_HELD",
  "TRIAL_ALREADY_USED",
  "BASE_ITEM_REQUIRED",
  // ── M7 outbound webhooks (ADR-0013) ──
  "WEBHOOK_URL_REFUSED",
  "WEBHOOK_ENDPOINT_LIMIT",
  "WEBHOOK_ENDPOINT_DISABLED",
  // ── M8 live stream and scoped roles ──
  "SCOPE_LIMITED",
  "STREAM_LIMIT",
  // ── M9 schedules, holidays and the terminal user id ──
  "SCHEDULE_IN_USE",
  "SCHEDULE_SLOTS_EXHAUSTED",
  "DEVICE_USER_ID_TAKEN",
  "DEVICE_USER_ID_LOCKED",
  // ── M14 §1 (ADR-0017 decision 6): person contacts and the duplicate warning ──
  "PERSON_DUPLICATE_SUSPECTED",
  // ── M17 (ADR-0017 addendum 2026-09-22): the same soft warning, one step earlier in the funnel ──
  "LEAD_DUPLICATE_SUSPECTED",
  // ── M14 §5 (ADR-0007 addendum): terminal capacity ──
  "DEVICE_CAPACITY",
  // ── M14 §2 (ADR-0018): time-bounded access grants ──
  "GRANT_WINDOW_INVALID",
  // ── M14 §4 (ADR-0019): the member ledger, and the idempotency plugin it widened ──
  "IDEMPOTENCY_KEY_REQUIRED",
  "PERSON_BALANCE_OUTSTANDING",
  // ── M19 (ADR-0015 addendum 2026-10-01): the Staff add-on is a prepaid quantity of employees ──
  "STAFF_QUANTITY_BELOW_ACTIVE",
  // ── M20 (ADR-0015 addendum 2026-10-02): Student Attendance per student; staff + students ≤ people capacity ──
  "STUDENT_QUANTITY_BELOW_ACTIVE",
  "PERSON_CAPACITY_EXCEEDED",
  // ── M25 (ADR-0015 addendum 2026-10-08, rule 4): Labour Attendance per labourer ──
  "LABOUR_QUANTITY_BELOW_ACTIVE",
  "INTERNAL",
] as const;
type ErrorCode = (typeof ERROR_CODES)[number];

/**
 * Client-side codes for failures that never reached the envelope, plus three the session client mints
 * itself (client.ts, plan §5.2/§5.6): `SERVER` is a renewal the api could not answer (5xx), so the request
 * that needed it fails as a blip and not as the 401 that would end the session; `SESSION_RENEWAL_REFUSED`
 * is a renewal refused for any other reason than a 401; `ORGANISATION_CHANGED` is a request dropped
 * because this tab no longer shows the organisation it was made in — nobody is told, the tab follows.
 */
type ClientErrorCode =
  | "NETWORK"
  | "TIMEOUT"
  | "CANCELLED"
  | "UNKNOWN"
  | "SERVER"
  | "SESSION_RENEWAL_REFUSED"
  | "ORGANISATION_CHANGED";

/**
 * The 401 codes on an ordinary request that mean "the access token expired — renew and retry"; 5xx and
 * network blips never log anyone out (rules/01 §2). Whether the session is actually over is the renewal's
 * own answer: only a 401 from `/auth/refresh` ends it (client.ts, ADR-0011 addendum 2026-10-04).
 */
export const RELOGIN_REQUIRED_CODES: ReadonlySet<string> = new Set(["UNAUTHORIZED"]);

interface ValidationIssue {
  path: string;
  message: string;
}

export class ApiError extends Error {
  readonly code: ErrorCode | ClientErrorCode | (string & {});
  readonly status: number | null;
  readonly details: unknown;
  readonly requestId: string | null;
  /** A 429's `Retry-After`, in seconds, when the browser could read the header (same origin or exposed). */
  readonly retryAfterSeconds: number | null;

  constructor(input: {
    code: string;
    message: string;
    status?: number | null;
    details?: unknown;
    requestId?: string | null;
    retryAfterSeconds?: number | null;
  }) {
    super(input.message);
    this.name = "ApiError";
    this.code = input.code;
    this.status = input.status ?? null;
    this.details = input.details;
    this.requestId = input.requestId ?? null;
    this.retryAfterSeconds = input.retryAfterSeconds ?? null;
  }

  /** 422 payloads carry `{path, message}[]`; anything else is treated as opaque. */
  get validationIssues(): ValidationIssue[] {
    if (!Array.isArray(this.details)) return [];
    return this.details.filter(
      (d): d is ValidationIssue =>
        typeof d === "object" && d !== null && typeof (d as ValidationIssue).path === "string",
    );
  }

  /** The object form of `details` (`PLAN_LIMIT_REACHED` carries `{ resource, limit, used, planName }`); null otherwise. */
  get detailObject(): Record<string, unknown> | null {
    if (typeof this.details !== "object" || this.details === null || Array.isArray(this.details)) return null;
    return this.details as Record<string, unknown>;
  }

  /** A string field from an object `details` payload (`CARD_IN_USE` carries `{ cardNo }`); null otherwise. */
  detailString(key: string): string | null {
    const value = this.detailObject?.[key];
    return typeof value === "string" ? value : null;
  }

  detailNumber(key: string): number | null {
    const value = this.detailObject?.[key];
    return typeof value === "number" && Number.isFinite(value) ? value : null;
  }

  /** How long a 429 asks the caller to wait: the header, else `details.retryAfterSeconds`; null when it says nothing. */
  get retryAfterMs(): number | null {
    const seconds = this.retryAfterSeconds ?? this.detailNumber("retryAfterSeconds");
    return seconds === null ? null : Math.max(0, seconds) * 1000;
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/** The api's own generic messages: never show one to a customer, whatever code carries it. */
const GENERIC_REFUSAL = /^(validation failed|bad request|internal server error|unprocessable entity)\.?$/i;

/**
 * Why an endpoint URL was refused (ADR-0013 SSRF policy). The api sends the reason as a code in
 * `details.reason`; anything it adds later falls back to its own sentence.
 */
const WEBHOOK_REFUSAL_REASONS: Record<string, string> = {
  scheme: "Only https:// addresses can receive webhooks.",
  hostname: "That host name cannot be used; give a public address.",
  unresolvable: "That host name does not resolve. Check the spelling.",
  loopback: "That address is on this machine, not the public internet.",
  unspecified: "That address is not a real destination.",
  private: "That address is on a private network we cannot reach.",
  link_local: "That address is link-local, not reachable from the internet.",
  metadata: "That address is a cloud metadata address and is never dialled.",
  multicast: "That address is a multicast address, not a receiver.",
  reserved: "That address is in a reserved range.",
};

function webhookUrlReason(error: ApiError): string {
  const reason = error.detailString("reason");
  const known = reason ? WEBHOOK_REFUSAL_REASONS[reason] : undefined;
  return known ?? "Use a public https:// address; private and local addresses are refused.";
}

/** Plain-language summary for toasts and failure cards. Technical detail stays in `detail`. */
export function describeError(error: unknown): { title: string; detail: string | null } {
  if (isApiError(error)) {
    // The api writes a plain sentence for most refusals ("The name does not match") and that stays
    // the headline. Its generic fallbacks are developer strings, though, and one of them was the
    // headline on the device page for a mistyped link: "Validation failed" (design review 2026-09).
    if (GENERIC_REFUSAL.test(error.message.trim())) {
      return {
        title: "Some details were not accepted",
        detail: "Check what you entered, or the link you followed, and try again.",
      };
    }
    switch (error.code) {
      case "NETWORK":
        return { title: "Cannot reach the server", detail: "Check your connection and try again." };
      case "TIMEOUT":
        return { title: "The server took too long", detail: "Try again in a moment." };
      case "SERVER":
        // A renewal the api could not answer (a deploy, a restart). The session is intact (plan §5.2).
        return {
          title: "The server is not answering",
          detail: "You are still signed in. Try again in a moment.",
        };
      case "UNAUTHORIZED":
        return { title: "Your session has ended", detail: "Sign in again to continue." };
      case "MEMBERSHIP_INACTIVE":
        return { title: "Your access to this organisation has been turned off", detail: null };
      case "TENANT_DELETED":
        return { title: "This organisation has been deleted", detail: null };
      case "SESSION_RENEWAL_REFUSED":
        return {
          title: "Your session could not be renewed",
          detail: "Try again. If it keeps happening, sign in again.",
        };
      case "ORGANISATION_CHANGED":
      case "ORG_MISMATCH":
        // Never toasted on purpose (the move handler clears what is left); this is the fallback wording.
        return {
          title: "This tab now shows another organisation",
          detail: "The organisation was changed, so this page was closed. Nothing was saved from it.",
        };
      case "RELOAD_REQUIRED":
        return { title: "This page is out of date", detail: "Reload it to continue." };
      case "FORBIDDEN":
        return { title: "You do not have access to this", detail: error.message };
      case "NOT_FOUND":
        return { title: "Not found", detail: "It may have been removed, or the link is wrong." };
      case "RATE_LIMITED":
        return { title: "Too many requests", detail: "Wait a moment and try again." };
      case "PLAN_LIMIT_REACHED":
        // The server's sentence names the limit and the usage ("Your plan allows 2 terminals; 2 in use"),
        // and the limit notices offer the purchase in place. Quantities are prepaid since M11, so a full
        // limit is something the owner can buy their way past — not a sales enquiry (ADR-0015 §2).
        return {
          title: "Plan limit reached",
          detail: error.message || "Add more in Settings → Subscription to raise this limit.",
        };
      case "TRIAL_EXPIRED":
        return {
          title: "Your trial has ended",
          detail: "Reading still works, changes are paused. Contact Nexborg to activate your organisation.",
        };
      case "TENANT_SUSPENDED":
        return {
          title: "This organisation is suspended",
          detail: "Contact Nexborg to restore access. Nothing has been deleted.",
        };
      case "TENANT_DELETING":
        return {
          title: "This organisation is scheduled for deletion",
          detail:
            "Changes are paused until it is deleted or the deletion is cancelled. An owner can cancel it under Settings → Danger zone.",
        };
      case "API_KEY_INVALID":
        return {
          title: "This API key is not valid",
          detail: "It is unknown, revoked or expired. Create a new key under Settings → API keys.",
        };
      case "IDEMPOTENCY_MISMATCH":
        return {
          title: "This request key was already used for a different request",
          detail: "Send a new Idempotency-Key for a new request.",
        };
      case "IDEMPOTENCY_IN_PROGRESS":
        return {
          title: "The same request is still being processed",
          detail: "Wait for the first attempt to finish before repeating it.",
        };
      case "SCOPE_LIMITED":
        // The api's sentence names the way out ("ask an administrator with full access"), and the
        // portal hides these actions from a scoped role anyway — this is the deep-link case.
        return { title: "Your role is limited to some terminals", detail: error.message };
      case "STREAM_LIMIT":
        return {
          title: "Too many open tabs for live updates",
          detail: "Close another NexBorg Access tab; this one refreshes on a timer meanwhile.",
        };
      case "OPERATOR_REQUIRED":
        return {
          title: "Only Nexborg operators can do this",
          detail: "It is not part of the customer portal.",
        };
      case "DEVICE_OFFLINE":
        return {
          title: "The device is offline",
          detail: "The terminal is not connected right now. Check its power and network, then try again.",
        };
      case "DEVICE_DISABLED":
        return {
          title: "The device is disabled",
          detail: "It rejects commands until it is enabled again from its device page.",
        };
      case "DEVICE_ALREADY_PAIRED":
        return {
          title: "Already paired to an organisation",
          detail: "Unpair it there first, or check the serial on the label.",
        };
      case "DEVICE_REJECTED":
        // The device's own words are the only explanation there is.
        return { title: "The device refused the command", detail: error.message };
      case "GATEWAY_UNAVAILABLE":
        return {
          title: "The device service is not answering",
          detail: "Try again in a moment. If it keeps happening, contact support.",
        };
      case "PAYMENT_GATEWAY_UNAVAILABLE":
        return {
          title: "The payment service is not answering",
          detail: "Nothing was charged. Try again in a minute; your invoice is created only once it answers.",
        };
      case "SMS_UNAVAILABLE":
        return {
          title: "Could not send the code",
          detail: "The SMS service is not answering. Try again in a minute.",
        };
      case "INVITE_EXPIRED":
        return { title: "This invitation has expired", detail: "Ask the organisation to invite you again." };
      case "INVITE_NOT_OPEN":
        return {
          title: "This invitation is no longer open",
          detail: "It was accepted, declined or withdrawn.",
        };
      case "INVITE_NOT_FOUND":
        return { title: "Invitation not found", detail: "It may have been withdrawn." };
      case "CARD_IN_USE": {
        const cardNo = error.detailString("cardNo");
        return {
          title: cardNo ? `Card ${cardNo} is already assigned` : "This card number is already assigned",
          detail: /twice/i.test(error.message)
            ? "The same number is listed twice; keep one."
            : "Each card belongs to one person. Remove it from the other person first.",
        };
      }
      case "PHOTO_INVALID":
        // The check's own words ("no face found", "too small") are the only useful explanation.
        return { title: "The photo cannot be used", detail: error.message };
      case "FACE_PHOTO_UNUSABLE":
        return {
          title: facePhotoUnusableWords(error),
          detail: FACE_PHOTO_UNUSABLE_DETAIL,
        };
      case "IMPORT_INVALID":
        // The row table shows what is wrong with each line; the message carries the count or the parse error.
        return { title: "The import file has errors", detail: error.message };
      // ── M7 billing (ADR-0012) ──
      case "BILLING_PROFILE_REQUIRED":
        // Every purchase dialog asks for them in place (`BillingProfileStep`); this is the fallback wording.
        return {
          title: "Billing details are needed first",
          detail: "They go on the invoice. Add them under Billing details in Settings → Subscription.",
        };
      case "PLAN_DOWNGRADE_BLOCKED":
        // The dialog renders the blockers from `details`; this is the fallback wording.
        return {
          title: "This is smaller than what you use today",
          detail: "Remove what does not fit before scheduling the reduction.",
        };
      // ── M11 catalogue, prepaid quantities and orders (ADR-0015) ──
      case "ADDON_REQUIRED":
        // The gate panels read `details` and offer the fix in place; this is the toast fallback.
        return {
          title: "This needs an add-on you do not have",
          detail: "Open Settings → Subscription to try it free or add it.",
        };
      case "TRIAL_ALREADY_USED":
        return {
          title: "This has already been trialled",
          detail: "A free trial runs once per add-on. Add it to your subscription to switch it back on.",
        };
      case "BASE_ITEM_REQUIRED":
        return {
          title: "The base plan cannot be removed",
          detail:
            "Every organisation keeps at least one terminal. Delete the organisation in Danger zone instead.",
        };
      case "PERIOD_CHANGE_PENDING":
        return {
          title: "A period change is already scheduled",
          detail: "It takes effect at your next renewal. Undo that one first to choose differently.",
        };
      // ── M11 §14: the timing choices and cancelling the subscription (ADR-0015 addendum) ──
      case "PERIOD_CHANGE_AT_RENEWAL_ONLY":
        return {
          title: "This period change happens at your renewal",
          detail:
            "Only monthly to yearly can be paid for today. Yearly to monthly takes effect on your renewal date, and nothing is charged or refunded now.",
        };
      case "SUBSCRIPTION_IN_TRIAL":
        return {
          title: "A trial has nothing to cancel",
          detail: "It simply ends on its own date and you are never charged. Nothing is deleted.",
        };
      case "SUBSCRIPTION_NOT_ACTIVE":
        return {
          title: "There is no running subscription to change",
          detail: "Refresh the page to see where this organisation stands.",
        };
      // ── M11 follow-up: what "Renew now" can be refused for ──
      case "SUBSCRIPTION_CANCELLING":
        return {
          title: "This subscription is set to end",
          detail:
            "There is no renewal to pay for. Resume it first, and then the next period can be paid up front.",
        };
      case "ORDER_OPEN":
        // The page opens that order instead of repeating this; the toast is the fallback.
        return {
          title: "There is already an invoice waiting",
          detail: "Pay the open one under Payments; a second invoice for the same period is not raised.",
        };
      case "QUANTITY_OUT_OF_RANGE": {
        // M19 rule 2: Staff above the people capacity carries both numbers in `details`, and the sentence
        // that names them is ours. Every other item keeps the api's own range.
        const staff = error.detailNumber("quantity");
        const capacity = error.detailNumber("peopleCapacity");
        if (staff !== null && capacity !== null) {
          return {
            title: "Staff cannot be more than the people your plan holds",
            detail: staffAbovePeopleSentence(staff, capacity),
          };
        }
        // The api names the item and the range it allows; the stepper puts that on the row.
        return { title: error.message, detail: "Choose a quantity inside the range shown." };
      }
      case "STAFF_QUANTITY_BELOW_ACTIVE":
      case "STUDENT_QUANTITY_BELOW_ACTIVE":
      case "LABOUR_QUANTITY_BELOW_ACTIVE": {
        // M19/M20/M25 rule 3, per kind: both numbers come in `details`; the sentence names them and says what to
        // do first (exit employees, withdraw students, release labourers).
        const kind = personKindOfBelowActiveCode(error.code)!;
        const active = error.detailNumber("active");
        const requested = error.detailNumber("requested");
        return {
          title: personBelowActiveTitle(kind),
          detail:
            active !== null && requested !== null
              ? personBelowActiveSentence(kind, active, requested)
              : personBelowActiveFallback(kind),
        };
      }
      case "PERSON_CAPACITY_EXCEEDED": {
        // M20/M25 rule 2, combined: Staff + students + labourers together inside the people capacity. `details`
        // carries the capacity and every quantity as it would stand; the sentence that adds them up is ours.
        const capacity = error.detailNumber("peopleCapacity");
        const quantities = {
          staff: error.detailNumber("staff") ?? 0,
          students: error.detailNumber("students") ?? 0,
          labourers: error.detailNumber("labourers") ?? 0,
        };
        return {
          title: personCapacityTitle(quantities),
          detail:
            capacity !== null
              ? personCapacitySentence(capacity, quantities)
              : "Staff and students together cannot be more than the people your plan holds. Add terminals or extra people first.",
        };
      }
      case "ORDER_NOT_PAYABLE":
        return {
          title: "This order can no longer be paid",
          detail: "It was already paid, cancelled or re-issued. Refresh to see where it stands.",
        };
      case "ORDER_NOT_FOUND":
        return { title: "That order is no longer here", detail: "Refresh the page to see the current ones." };
      case "ITEM_NOT_HELD":
        return { title: "You do not have that item", detail: "Refresh the page to see your subscription." };
      case "CATALOG_ITEM_UNKNOWN":
      case "CATALOG_ITEM_INACTIVE":
        return {
          title: "That is not on sale",
          detail: "Refresh the page; what is available may have changed.",
        };
      case "CATALOG_PRICE_MISSING":
      case "CATALOG_TAX_MISSING":
        return {
          title: "This cannot be billed yet",
          detail: "It has no price or tax code set. Contact Nexborg and we will sort it out.",
        };
      // ── M7 outbound webhooks (ADR-0013) ──
      case "WEBHOOK_URL_REFUSED":
        return { title: "This address cannot receive webhooks", detail: webhookUrlReason(error) };
      case "WEBHOOK_ENDPOINT_LIMIT":
        return {
          title: "You already have the maximum number of endpoints",
          detail: "Delete one you no longer use, then add this one.",
        };
      case "WEBHOOK_ENDPOINT_DISABLED":
        return {
          title: "This endpoint is switched off",
          detail: "Fix the receiver, then press Resume before changing it.",
        };
      // ── M9 (plans/v1/M9-product.md §3, §5) ──
      // All four carry the fact in the api's own sentence and the specifics in `details`; the forms
      // that raise them put that sentence on the field, so the toast repeats it rather than inventing one.
      case "DEVICE_USER_ID_TAKEN":
        return {
          title: error.message,
          detail:
            "Terminal IDs are unique in an organisation. Choose another, or leave it blank to be assigned one.",
        };
      case "DEVICE_USER_ID_LOCKED":
        return {
          title: error.message,
          detail:
            "The terminals key this person by that id, so it can only be set before their first sync. Everything else about them can still change.",
        };
      case "SCHEDULE_IN_USE":
        return {
          title: error.message,
          detail: "Move the people on it to another schedule (or to Unrestricted) before deleting it.",
        };
      case "SCHEDULE_SLOTS_EXHAUSTED":
        return {
          title: error.message,
          detail:
            "A terminal holds a limited number of templates. Reuse a schedule this terminal already has, or take one off the people who no longer need it.",
        };
      // ── M14 §1 (ADR-0017 decision 6) ──
      case "PERSON_DUPLICATE_SUSPECTED":
        // The add-person dialog and the import preview read `details.matches` and show who was
        // matched, with a way to confirm and resend as `allowDuplicate`; this is the fallback for
        // anywhere else the code surfaces.
        return {
          title: "This may already be someone in your organisation",
          detail: "Check the match below. Two people can genuinely share a number or address.",
        };
      // ── M14 §5 (ADR-0007 addendum): terminal capacity ──
      case "DEVICE_CAPACITY":
        // The api's own sentence names the model, the limit and the numbers; nothing here repeats them.
        return {
          title: error.message,
          detail: "Free up room on this terminal, or add fewer people at once.",
        };
      // ── M14 §2 (ADR-0018): time-bounded access grants ──
      case "GRANT_WINDOW_INVALID":
        return { title: "This window is not valid", detail: "The end must come after the start." };
      // ── M14 §4 (ADR-0019): the member ledger ──
      case "IDEMPOTENCY_KEY_REQUIRED":
        // The client always sends one on these four routes; seeing this means the request was built
        // by hand or a proxy stripped the header, not a customer mistake.
        return {
          title: "This request was missing a required header",
          detail: "Reload the page and try again. If it keeps happening, contact support.",
        };
      case "PERSON_BALANCE_OUTSTANDING":
        // The delete dialog reads `details.balancePaise` and offers "Delete anyway" in place; this is
        // the fallback for anywhere else the code surfaces.
        return {
          title: "This person's account is not settled",
          detail: "Check their ledger balance before archiving them.",
        };
      case "MEMBER_GUARD":
      case "ROLE_IN_USE":
      case "CONFLICT":
        // The server's message is the explanation ("You cannot change your own role"); the code is the detail.
        return { title: error.message, detail: null };
      default:
        return {
          title: error.message || "Something went wrong",
          detail:
            [error.code, error.requestId ? `request ${error.requestId}` : null].filter(Boolean).join(" · ") ||
            null,
        };
    }
  }
  if (error instanceof Error) return { title: "Something went wrong", detail: error.message };
  return { title: "Something went wrong", detail: null };
}

/**
 * The one string worth reading out to support (redesign 2026-09, pass 02 board `6a`: the failure card
 * ends `ref EVT-503-a91f · copy`).
 *
 * It is deliberately NOT the technical detail: a reader who is on the phone needs one short token they
 * can say, not a stack. The api already mints exactly that — `x-request-id`, carried on every envelope
 * — so nothing is invented here, and a failure that never reached the api has no reference at all
 * rather than a made-up one.
 */
export function errorReference(error: unknown): string | null {
  return isApiError(error) ? error.requestId : null;
}

/**
 * Kumar 2026-10-08 — the server's upload-time face check (422 `FACE_PHOTO_UNUSABLE`, `details.reason`). The
 * reason in plain words, mapped from the code and the reason — never the api's message string. `dark` and
 * `bright` share one sentence; an unknown reason (a newer server) reads as the generic refusal.
 */
export const FACE_PHOTO_UNUSABLE_WORDS: Readonly<Record<string, string>> = {
  none: "No face found",
  many: "More than one face",
  small: "Face too small — use a closer photo",
  blur: "Too blurred",
  pose: "Face turned away",
  dark: "Too dark or too bright",
  bright: "Too dark or too bright",
};
export const FACE_PHOTO_UNUSABLE_DETAIL =
  "The photo was not saved and the previous one is kept. Use a sharp, frontal photo of one person in even light.";

/** The headline for a `FACE_PHOTO_UNUSABLE` refusal ("No face found", …). */
export function facePhotoUnusableWords(error: ApiError): string {
  const reason =
    error.details && typeof error.details === "object"
      ? (error.details as { reason?: unknown }).reason
      : undefined;
  return (
    (typeof reason === "string" && FACE_PHOTO_UNUSABLE_WORDS[reason]) ||
    "This photo cannot be used for face recognition"
  );
}
